/**
 * Access control for the POS: what the signed-in person's role allows.
 *
 * `/auth/me` returns `permissions` (a list of keys) and `role` ({ id, code, name }).
 * The keys and what each built-in role gets live in the backend's
 * app/services/permissions.py; Admins manage roles on the Users > Roles page.
 */

export const PERMS = {
  POS: 'pos',
  SCANNER: 'scanner',
  ITEMS: 'items',
  SALES: 'sales',
  SALE_RETURNS: 'sale_returns',
  EXCHANGES: 'exchanges',
  PURCHASES: 'purchases',
  PURCHASE_RETURNS: 'purchase_returns',
  EXPENSES: 'expenses',
  DAMAGES: 'damages',
  CLOSINGS: 'closings',
  ANALYTICS: 'analytics',
  REPORTS: 'reports',
  BUSINESS_INFO: 'business_info',
  USERS: 'users',
  REPORTS_MANAGE: 'reports.manage',
  SALES_EDIT_PAYMENT: 'sales.edit_payment',
  DAMAGES_SEND: 'damages.send',
  ITEMS_SYNC_ALL: 'items.sync_all',
};

/** True when the person's role grants `key` (or every key in an array). */
export const can = (user, key) => {
  const granted = Array.isArray(user?.permissions) ? user.permissions : [];
  const keys = Array.isArray(key) ? key : [key];
  return keys.every((k) => granted.includes(k));
};

export const isAdminRole = (user) => user?.role?.code === 'admin';

/**
 * The menu (Ctrl+M), in its fixed order: the number and F-key shortcuts follow this
 * order, so new pages go at the end. `feature` = only on branches with that feature on.
 */
export const MENU_PAGES = [
  { permission: PERMS.POS, path: '/', label: 'POS', description: 'Point of Sale' },
  { permission: PERMS.SCANNER, path: '/scanner', label: 'Scanner', description: 'Barcode scanner' },
  { permission: PERMS.ITEMS, path: '/items', label: 'Items', description: 'Manage inventory' },
  { permission: PERMS.SALES, path: '/sales', label: 'Sales', description: 'View sales history' },
  { permission: PERMS.SALE_RETURNS, path: '/sale-return', label: 'Sale Returns', description: 'Process returns' },
  { permission: PERMS.EXCHANGES, path: '/exchange', label: 'Exchanges', description: 'Handle exchanges' },
  { permission: PERMS.PURCHASES, path: '/purchases', label: 'Purchases', description: 'Manage purchases' },
  { permission: PERMS.PURCHASE_RETURNS, path: '/purchase-returns', label: 'Purchase Returns', description: 'Manage purchase returns' },
  { permission: PERMS.EXPENSES, path: '/expenses', label: 'Expenses', description: 'Track daily expenses' },
  { permission: PERMS.CLOSINGS, path: '/closings', label: 'Closing', description: 'Daily closing operations' },
  { permission: PERMS.ANALYTICS, path: '/analytics2', label: 'Analytics', description: 'View reports and analytics' },
  { permission: PERMS.REPORTS, path: '/reports', label: 'Reports', description: 'Build and run custom reports' },
  { permission: PERMS.BUSINESS_INFO, path: '/business-information', label: 'Business Info', description: 'Manage business settings' },
  { permission: PERMS.DAMAGES, path: '/damages', label: 'Damages', description: 'Damaged stock and warehouse shipments', feature: 'damages' },
  { permission: PERMS.USERS, path: '/users', label: 'Users', description: 'People, passwords and roles' },
];

/** The menu entries this person may open on this branch. */
export const menuPagesFor = (user, features) =>
  MENU_PAGES.filter((p) => can(user, p.permission) && (!p.feature || features?.[p.feature]));

/** Where this person starts (after sign-in, and at `/`): their first menu page. */
export const startPathFor = (user, features) => menuPagesFor(user, features)[0]?.path || null;

// Kept for the pages that already use these names.
export const hasAnalyticsAccess = (user) => can(user, PERMS.ANALYTICS);
export const hasReportsAccess = (user) => can(user, PERMS.REPORTS);
export const canManageReports = (user) => can(user, [PERMS.REPORTS, PERMS.REPORTS_MANAGE]);
